import React, { useEffect, useState } from 'react';
import { supabase, lookupCompanyInCVR } from '../lib/supabase';
import { formatDate } from '../utils/dateFormat';
import { ALLOWED_ADMINS } from '../lib/supabase';
import LeadDetails from '../components/Leads/LeadDetails';
import CreateLeadModal from '../components/Leads/CreateLeadModal';
import { Lead, CVRAPIResponse } from '../types/lead';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';

const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost', 'not relevant'] as const;

type SortField = 'created_at' | 'job_count' | 'interactions' | 'company' | 'contact_name';
type SortDirection = 'asc' | 'desc';

interface JobCount {
  lead_id: string;
  count: number;
}

const Leads: React.FC = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [checkingAllCVR, setCheckingAllCVR] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showRelevantOnly, setShowRelevantOnly] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [checkingCVR, setCheckingCVR] = useState<{ [key: string]: boolean }>({});
  const [cvrMessages, setCvrMessages] = useState<{ [key: string]: string }>({});
  const [hideProtectedCompanies, setHideProtectedCompanies] = useState(true);
  const [hidePublicOrgs, setHidePublicOrgs] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    
    fetchLeads();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchLeads = async () => {
    try {
      console.log('Fetching leads...');
      if (!user) {
        console.error('No authenticated user');
        return;
      }
      
      console.log('Authenticated as:', user.email);

      // First get the leads without job counts
      const { data: leadsData, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Then get the job counts using our database function
      const { data: jobCounts, error: countError } = await supabase
        .rpc('get_lead_job_counts');

      if (countError) {
        console.error('Error fetching job counts:', countError);
        throw countError;
      }

      // Create a map of lead_id to job count
      const jobCountMap = new Map(
        (jobCounts as JobCount[])?.map(item => [item.lead_id, item.count]) || []
      );

      // Transform the data to include the correct job count
      const leadsWithCounts = leadsData?.map(lead => ({
        ...lead,
        job_count: jobCountMap.get(lead.id) || 0
      })) || [];

      console.log(`Processed ${leadsWithCounts.length} leads with job counts`);
      console.log('Total jobs:', Array.from(jobCountMap.values()).reduce((sum, count) => sum + count, 0));

      setLeads(leadsWithCounts);
      setFilteredLeads(leadsWithCounts);
    } catch (error) {
      console.error('Error fetching leads:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const sortLeads = (leadsToSort: Lead[]) => {
    return [...leadsToSort].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'job_count':
          comparison = (a.job_count || 0) - (b.job_count || 0);
          break;
        case 'interactions':
          comparison = (a.interactions?.length || 0) - (b.interactions?.length || 0);
          break;
        case 'company':
          comparison = (a.company || '').localeCompare(b.company || '');
          break;
        case 'contact_name':
          comparison = (a.contact_name || '').localeCompare(b.contact_name || '');
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  useEffect(() => {
    let filtered = leads.filter(lead => {
      // First check if lead has any contact information
      const hasContactInfo = lead.contact_name || lead.contact_email || lead.contact_phone;
      if (!hasContactInfo) return false;

      // Check advertising protection filter
      if (hideProtectedCompanies && String(lead.advertising_protection || '').toLowerCase().includes('protect')) {
        return false;
      }

      // Check public organizations filter
      if (hidePublicOrgs && String(lead.company_activities || '').toLowerCase().match(/(stat|region|kirke|kommune)/)) {
        return false;
      }

      const searchTermLower = String(searchTerm || '').toLowerCase();
      const matchesSearch = searchTerm === '' || [
        String(lead.contact_name || ''),
        String(lead.contact_email || ''),
        String(lead.company || ''),
        String(lead.contact_phone || '')
      ].some(field => field.toLowerCase().includes(searchTermLower));
      
      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || lead.referrer === sourceFilter;
      const matchesAssignedTo = assignedToFilter === 'all' || lead.assigned_to === assignedToFilter;
      const isRelevant = !showRelevantOnly || (lead.status !== 'lost' && lead.status !== 'not relevant');
      
      return matchesSearch && matchesStatus && matchesSource && matchesAssignedTo && isRelevant;
    });
    
    // Apply sorting
    filtered = sortLeads(filtered);
    
    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, sourceFilter, assignedToFilter, leads, sortField, sortDirection, showRelevantOnly, hideProtectedCompanies, hidePublicOrgs]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleAssign = async (leadId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ assigned_to: email })
        .eq('id', leadId);

      if (error) throw error;

      // Log the assignment activity
      const lead = leads.find(l => l.id === leadId);
      try {
        const { error: logError } = await supabase
          .from('activity_logs')
          .insert({
            user_email: user?.email || 'unknown',
            action_type: 'assignment',
            details: `Assigned to ${email}`,
            lead_id: leadId,
            lead_company: lead?.company || ''
          });

        if (logError) {
          console.error('Error logging activity:', logError);
        }
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }
      
      // Refresh leads after assignment
      fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      const oldStatus = lead?.status;

      const { error } = await supabase
        .from('leads')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId);

      if (error) throw error;

      // Log the status change activity
      try {
        const { error: logError } = await supabase
          .from('activity_logs')
          .insert({
            user_email: user?.email || 'unknown',
            action_type: 'status_change',
            details: `Status changed from ${oldStatus} to ${newStatus}`,
            lead_id: leadId,
            lead_company: lead?.company || ''
          });

        if (logError) {
          console.error('Error logging activity:', logError);
        }
      } catch (logError) {
        console.error('Failed to log activity:', logError);
      }
      
      // Refresh leads after status update
      fetchLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const handleCheckCVR = async (leadId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setCheckingCVR(prev => ({ ...prev, [leadId]: true }));
    setCvrMessages(prev => ({ ...prev, [leadId]: '' }));
    
    try {
      const lead = leads.find(l => l.id === leadId);
      console.log(`Checking CVR for ${lead?.company || 'Unknown Company'}`);
      
      const response = await lookupCompanyInCVR(leadId);
      const cvrData = response.data as CVRAPIResponse;
      
      // Update the cvr_status in the database based on whether we got data
      const hasData = cvrData && (
        cvrData.vat || // CVR number
        cvrData.employees || // Employee count
        cvrData.industrycode || // Industry code
        cvrData.industrydesc // Industry description
      );

      const { error: updateError } = await supabase
        .from('leads')
        .update({ 
          cvr_status: hasData ? 'checked_with_data' : 'checked_no_data',
          cvr: cvrData?.vat || null,
          company_size: cvrData?.employees || null,
          industry_code: cvrData?.industrycode || null,
          company_activities: cvrData?.industrydesc || null,
          advertising_protection: cvrData?.reklame === '1' ? 'protected' : 'not_protected'
        })
        .eq('id', leadId);

      if (updateError) {
        console.error('Error updating CVR status:', updateError);
      }

      // Set a user-friendly message
      setCvrMessages(prev => ({ 
        ...prev, 
        [leadId]: hasData 
          ? `Found CVR: ${cvrData.vat}${cvrData.reklame === '1' ? ' (Protected)' : ''}`
          : 'No CVR data found'
      }));
      
      await fetchLeads();
    } catch (error) {
      console.error('Error checking CVR:', error);
      setCvrMessages(prev => ({ ...prev, [leadId]: 'Failed to check CVR data. Please try again.' }));
    } finally {
      setCheckingCVR(prev => ({ ...prev, [leadId]: false }));
      // Clear message after 5 seconds
      setTimeout(() => {
        setCvrMessages(prev => {
          const newMessages = { ...prev };
          delete newMessages[leadId];
          return newMessages;
        });
      }, 5000);
    }
  };

  const handleCheckAllCVR = async () => {
    setCheckingAllCVR(true);
    try {
      // Get all leads without CVR status
      const uncheckedLeads = leads.filter(lead => 
        !lead.cvr_status || 
        (lead.cvr_status !== 'checked_with_data' && lead.cvr_status !== 'checked_no_data')
      );
      
      console.log(`Starting batch CVR check for ${uncheckedLeads.length} companies`);
      
      // Process in batches of 5 to avoid overwhelming the API
      const batchSize = 5;
      let processedCount = 0;
      
      for (let i = 0; i < uncheckedLeads.length; i += batchSize) {
        const batch = uncheckedLeads.slice(i, i + batchSize);
        await Promise.all(batch.map(lead => lookupCompanyInCVR(lead.id)));
        processedCount += batch.length;
        console.log(`Processed ${processedCount}/${uncheckedLeads.length} companies`);
      }
      
      console.log('Batch CVR check completed');
      await fetchLeads();
    } catch (error) {
      console.error('Error checking all CVR:', error);
      alert('An error occurred while checking CVR data. Some records may not have been processed.');
    } finally {
      setCheckingAllCVR(false);
    }
  };

  const uniqueStatuses = Array.from(new Set(leads.map(lead => lead.status))).filter(Boolean);
  const uniqueSources = Array.from(new Set(leads.map(lead => lead.referrer))).filter(Boolean);

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const triggerScraper = async () => {
    try {
      setScraping(true);
      const { data, error } = await supabase.functions.invoke('trigger-scraper');

      if (error) {
        console.error('Error triggering scraper:', error);
        alert('Failed to trigger scraper. Please try again.');
      } else if (data?.success) {
        alert('Scraper started successfully! New leads will appear soon.');
        
        // Start polling for task status
        const taskId = data.taskId;
        const pollInterval = setInterval(async () => {
          const { data: taskData, error: taskError } = await supabase
            .from('scraper_tasks')
            .select('status, error')
            .eq('id', taskId)
            .single();
          
          if (taskError) {
            console.error('Error checking task status:', taskError);
            return;
          }
          
          if (taskData?.status === 'completed') {
            clearInterval(pollInterval);
            setScraping(false);
            fetchLeads();
          } else if (taskData?.status === 'failed') {
            clearInterval(pollInterval);
            setScraping(false);
            alert(`Scraper failed: ${taskData.error || 'Unknown error'}`);
          }
        }, 5000); // Check every 5 seconds
        
        // Stop polling after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setScraping(false);
        }, 5 * 60 * 1000);
      } else {
        alert(data?.message || 'Failed to trigger scraper. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('An error occurred while triggering the scraper.');
      setScraping(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leads</h1>
        <div className="flex gap-2">
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowCreateModal(true)}
            startIcon={<AddIcon />}
          >
            New Lead
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckAllCVR}
            disabled={checkingAllCVR}
            startIcon={<BusinessIcon />}
          >
            {checkingAllCVR ? 'Checking All CVR...' : 'Check All CVR'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={triggerScraper}
            disabled={scraping}
            startIcon={<RefreshIcon />}
          >
            {scraping ? 'Getting More...' : 'Get More Leads'}
          </Button>
        </div>
      </div>
      
      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Total Leads</h2>
          <p className="text-3xl font-bold text-blue-600">{filteredLeads.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Contacted Yesterday</h2>
          <p className="text-3xl font-bold text-green-600">
            {leads.filter(l => {
              if (!l.last_contacted_at) return false;
              const yesterday = new Date();
              yesterday.setDate(yesterday.getDate() - 1);
              yesterday.setHours(0, 0, 0, 0);
              const today = new Date();
              today.setHours(23, 59, 59, 999);
              const contactDate = new Date(l.last_contacted_at);
              return contactDate >= yesterday && contactDate <= today;
            }).length}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h2 className="text-lg font-semibold">Contacted Last 30 Days</h2>
          <p className="text-3xl font-bold text-purple-600">
            {leads.filter(l => {
              if (!l.last_contacted_at) return false;
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return new Date(l.last_contacted_at) > thirtyDaysAgo;
            }).length}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="col-span-1 md:col-span-2">
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Sources</option>
              {uniqueSources.map(source => (
                <option key={source || ''}>{source || 'Unknown'}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={assignedToFilter}
              onChange={(e) => setAssignedToFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Assigned</option>
              <option value="">Unassigned</option>
              {ALLOWED_ADMINS.map(email => (
                <option key={email} value={email}>{email}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Leads List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Leads</h3>
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={statusFilter === 'new'}
                onChange={(e) => setStatusFilter(e.target.checked ? 'new' : 'all')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">New Only</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={hideProtectedCompanies}
                onChange={(e) => setHideProtectedCompanies(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Hide Protected Companies</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={hidePublicOrgs}
                onChange={(e) => setHidePublicOrgs(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Hide Public Organizations</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showRelevantOnly}
                onChange={(e) => setShowRelevantOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Relevant Only</span>
            </label>
            <span className="text-sm text-gray-500">Showing: {filteredLeads.length} of {leads.length}</span>
          </div>
        </div>

        <div className="border-t border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="text-center py-4">Loading leads...</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('contact_name')}
                  >
                    Contact {renderSortIcon('contact_name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('job_count')}
                  >
                    Jobs {renderSortIcon('job_count')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('interactions')}
                  >
                    Interactions {renderSortIcon('interactions')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_at')}
                  >
                    Created {renderSortIcon('created_at')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <tr 
                    key={lead.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {lead.contact_name}
                        {lead.advertising_protection?.includes('protect') && (
                          <span className="ml-2 text-red-600 font-bold" title="Reklamebeskyttet">✕</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{lead.contact_email}</div>
                      {lead.contact_phone && (
                        <div className="text-sm text-gray-500">{lead.contact_phone}</div>
                      )}
                      {lead.company && (
                        <div className="text-xs text-gray-500 mt-1">{lead.company}</div>
                      )}
                      {cvrMessages[lead.id] && (
                        <div className="mt-2 p-2 bg-blue-50 text-blue-800 rounded text-xs whitespace-pre-line">
                          {cvrMessages[lead.id]}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{lead.referrer}</div>
                      {lead.utm_source && (
                        <div className="text-xs text-gray-500 mt-1">
                          {lead.utm_source} / {lead.utm_medium}
                        </div>
                      )}
                      {lead.source_details && (
                        <div className="text-xs text-gray-500 mt-1 italic">
                          {lead.source_details}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {lead.job_count} posts
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {lead.interactions?.length || 0} interactions
                      </div>
                      {lead.last_contacted_at && (
                        <div className="text-xs text-gray-500 mt-1">
                          Last: {formatDate(lead.last_contacted_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                        className={`px-2 py-1 text-xs font-semibold rounded-full w-full
                          ${lead.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                          focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      >
                        {LEAD_STATUSES.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={(lead.assigned_to || '') as string}
                        onChange={(e) => handleAssign(lead.id, e.target.value)}
                        className="block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Unassigned</option>
                        {ALLOWED_ADMINS.map(email => (
                          <option key={email} value={email}>
                            {email}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center space-x-2">
                        {!checkingCVR[lead.id] && (
                          <Button
                            variant="contained"
                            color={
                              lead.cvr_status === 'checked_with_data' ? "success" :
                              lead.cvr_status === 'checked_no_data' ? "error" :
                              "primary"
                            }
                            size="small"
                            onClick={(e) => !lead.cvr_status?.includes('checked') && handleCheckCVR(lead.id, e)}
                            startIcon={<BusinessIcon />}
                            disabled={lead.cvr_status?.includes('checked')}
                          >
                            {lead.cvr_status === 'checked_with_data' ? 'CVR Found' :
                             lead.cvr_status === 'checked_no_data' ? 'No CVR Data' :
                             'Check CVR'}
                          </Button>
                        )}
                        {checkingCVR[lead.id] && (
                          <span className="text-gray-500 text-sm">Checking...</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Lead Details Modal */}
      {selectedLead && (
        <LeadDetails
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={fetchLeads}
        />
      )}

      {/* Create Lead Modal */}
      {showCreateModal && (
        <CreateLeadModal
          onClose={() => setShowCreateModal(false)}
          onLeadCreated={fetchLeads}
        />
      )}
    </div>
  );
};

export default Leads; 